import { fhirClients } from "src-next/shared/api/fhirClient";
import type { FhirSearchParams } from "src-next/shared/api/types/fhir-types";
import { isTeleconsultoriaAffiliation } from "../helpers/isTeleconsultoriaAffiliation";
import { organizationReferenceFromToken } from "../helpers/affiliationReferences";

export const AFFILIATION_RESOURCE_TYPE = "OrganizationAffiliation";

/** Teto por busca. Uma prestadora atende poucas dezenas de organizações. */
const AFFILIATION_SEARCH_COUNT = "200";

export interface AffiliationSearchResult {
  affiliations: fhir5.OrganizationAffiliation[];
  /** Organizations vindas pelo `_include`, indexadas pelo id (`<oid>-<cnpj>`). */
  organizationsById: Map<string, fhir5.Organization>;
}

/**
 * Separa o bundle e mantém SÓ afiliações de teleconsultoria (HIE/HIO).
 * O filtro é feito aqui, no cliente, porque não confirmamos se o servidor
 * aceita o parâmetro `role`; assim convênio nunca entra no módulo.
 */
function splitAffiliationBundle(bundle: fhir5.Bundle): AffiliationSearchResult {
  const affiliations: fhir5.OrganizationAffiliation[] = [];
  const organizationsById = new Map<string, fhir5.Organization>();

  for (const entry of bundle.entry || []) {
    const resource = entry.resource;
    if (resource?.resourceType === "OrganizationAffiliation") {
      if (isTeleconsultoriaAffiliation(resource)) affiliations.push(resource);
    }
    if (resource?.resourceType === "Organization" && resource.id) {
      organizationsById.set(resource.id, resource);
    }
  }
  return { affiliations, organizationsById };
}

async function searchAffiliations(
  params: FhirSearchParams,
): Promise<AffiliationSearchResult> {
  const bundle = await fhirClients.admin.search<fhir5.OrganizationAffiliation>(
    AFFILIATION_RESOURCE_TYPE,
    { _count: AFFILIATION_SEARCH_COUNT, ...params },
  );
  return splitAffiliationBundle(bundle);
}

/** Afiliações em que a organização é PRESTADORA (inclui a receptora no bundle). */
export function searchProvidedAffiliations(
  providerToken: string,
): Promise<AffiliationSearchResult> {
  return searchAffiliations({
    "primary-organization": organizationReferenceFromToken(providerToken),
    _include: "OrganizationAffiliation:participating-organization",
  });
}

/** Afiliações em que a organização é RECEPTORA (inclui a prestadora no bundle). */
export function searchReceivedAffiliations(
  receiverToken: string,
): Promise<AffiliationSearchResult> {
  return searchAffiliations({
    "participating-organization": organizationReferenceFromToken(receiverToken),
    _include: "OrganizationAffiliation:primary-organization",
  });
}
